import { BehaviorSubject, Subject, debounceTime } from 'rxjs';
import { CoordinatorService } from './coordinator.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { inject } from '@angular/core';

export type CITATION_STYLE_ENTRY = {
    id: string;
    abbrev?: string;
    title?: string;
    template?: string;
    fn?: string;
};
export class Display$ {
    helpDialog: any;
    mouseOverDialog: any;
    menuDialog: any;
    standaloneDialog: any;

    isExternalLogin = false;

    dialog$ = new Subject<any>();
    focus$ = new Subject<void>();
    click$ = new Subject<MouseEvent>();

    lastDialogAction?: string;

    isBulkDisplayOn = false;

    buttonAction$ = new Subject<any>();

    error$ = new Subject<{
        message: string;
        action: string;
    }>();

    citationStyleMap = new Map<string, CITATION_STYLE_ENTRY>();
    private _currentCitationStyle = 'acs';

    private _markForCheck$ = new Subject<void>();
    markForCheck$ = this._markForCheck$.pipe(debounceTime(100));

    private _manualTableChange$ = new Subject<any>();
    manualTableChange$ = this._manualTableChange$.pipe(debounceTime(500));

    isStartDialogOpen = false;
    isInStepperFlow = false;

    delete$ = new BehaviorSubject<any>(undefined);
    private _snackBar: MatSnackBar;
    snackBarData$ = new BehaviorSubject<any>(undefined);

    defaultSnackbarComponent: any;

    private _isScreenTablet = false;
    private _isScreenTabletMax = false;
    private _isScreenTabletMin = false;

    private _itemDetailTabLs = [
        'subsets',
        'ratings',
        'tags',
        'keywords',
        'comments',
        'biblio',
        'resources',
        'attachments',
        'external',
    ];

    mainState?: string;
    scrollTop = 0;

    isImportUrlWidget = false;
    isSingleBody = false;

    isEditMode = false;
    searchFocus$ = new Subject();

    textMap: Map<string, string> = new Map();
    textMap$ = new BehaviorSubject(this.textMap);

    miniDialogLs = [];

    tickRefmanagerDisplay$ = new Subject();
    icons: any;

    constructor(private coords: CoordinatorService) {
        this._snackBar = inject(MatSnackBar);
        this.delete$.subscribe(() => {
            this.dialog$.next({
                action: `delete`,
            });
        });

        this.textMap$.subscribe((m) => {
            this.textMap = m;
        });
    }

    /**
     * Checks if the current site is one of the specified target sites.
     *
     * @param targetLs - string | string[]. Possible prefixes are:
     * - "@": 'Up to', example: "@developer" will target both local and developer sites.
     * - "!": 'Everything but', example: "!referentka" will target everything, but referentka.app and beta.referentka.app.
     * - "~": 'Only', example: "~beta" will target only beta sites.
     * - "&": 'Exactly', example: "&beta.referentka.app" will target only beta.referentka.app.
     *
     * Unrecognized target sites will throw an error (except direct target sites).
     */
    targetSite(targetLs: string | string[]) {
        if (!Array.isArray(targetLs)) targetLs = targetLs.split(',').map((t) => t.trim());
        targetLs.forEach((target) => {
            if (target === '@beta' || target === '~beta') return;
            if (target.startsWith('@') || target.startsWith('!')) target = target.substring(1);
            if (
                !target.startsWith('&') &&
                !['local', 'developer', 'referentka', 'memowl'].includes(target)
            )
                throw Error(`Unknown target site: ${target} in ${targetLs.join(', ')}`);
        });

        const host = window.location.hostname;
        let hasDirectTarget = false;
        for (const target of targetLs) {
            if (target.startsWith('&')) {
                hasDirectTarget = true;
                if (host === target.substring(1)) return true;
            }
        }

        if (hasDirectTarget) return false;

        if (host.endsWith('.ng')) {
            if (targetLs.includes('!local')) return false;
            if (targetLs.includes('~beta')) return false;
            return true;
        }
        if (host.endsWith('e99.web.app')) {
            if (targetLs.includes('@local')) return false;
            if (targetLs.includes('!developer')) return false;
            if (targetLs.includes('~beta')) return false;
            return true;
        }
        if (host.endsWith('referentka.app')) {
            if (targetLs.includes('@local')) return false;
            if (targetLs.includes('@developer')) return false;
            if (targetLs.includes('@memowl')) return false;
            if (targetLs.includes('!referentka')) return false;
            if (host.startsWith('beta')) {
                if (targetLs.includes('~beta')) return true;
            } else {
                if (targetLs.includes('@beta')) return false;
            }
            return true;
        }
        if (host.endsWith('memowl.app')) {
            if (targetLs.includes('@local')) return false;
            if (targetLs.includes('@developer')) return false;
            if (targetLs.includes('!memowl')) return false;
            if (host.startsWith('beta')) {
                if (targetLs.includes('~beta')) return true;
            } else {
                if (targetLs.includes('@beta')) return false;
            }
            return true;
        }
        return false;
    }

    markForCheck() {
        if (!this.coords.worker$.isReady || this.coords.script$.hasScriptInRun) return;
        this._markForCheck$.next();
        // Trigger centralized counter recalculation
        this.coords.counter$.requestRecalculation();
    }

    get citationStyleLs() {
        return Array.from(this.citationStyleMap.values());
    }

    public get currentCitationStyle() {
        return this._currentCitationStyle;
    }
    public set currentCitationStyle(value) {
        this._currentCitationStyle = value;
        this.markForCheck();
    }

    manualTableChange(target: any) {
        this._manualTableChange$.next(target);
    }

    openSnackBar(comp: any, data: { message: string }, duration = 2) {
        comp = comp || this.defaultSnackbarComponent;
        this._snackBar.openFromComponent(comp, {
            duration: duration * 1000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
        });
        this.snackBarData$.next(data);
    }

    closeSnackbar() {
        this._snackBar?.dismiss();
    }

    public get isScreenTablet() {
        return this._isScreenTablet;
    }

    checkScreenTablet() {
        this._isScreenTablet = window.innerWidth < 950;
    }

    get isScreenTabletMax() {
        return this._isScreenTabletMax;
    }

    checkScreenTabletMax(): void {
        this._isScreenTabletMax = window.innerWidth >= 1180;
    }

    get isScreenTabletMin() {
        return this._isScreenTabletMin;
    }

    checkScreenTabletMin() {
        this._isScreenTabletMin = window.innerWidth >= 850;
    }

    detailPanelsSetAll(isVisible: boolean) {
        this.coords.pin$.misc?.batch(
            Array.from(this.coords.display$.itemDetailPanelMap.values()).map((p) => p.minimizeTag),
            !isVisible,
        );
    }

    get itemDetailPanelMap() {
        class Panel {
            title: string;
            minimizeTag: string;
            panelTag: string;

            constructor(
                public coords: any,
                tag: string,
            ) {
                this.title = coords.text(tag);
                this.minimizeTag = `minimize-panel-${tag}`;
                this.panelTag = `detail-panel-${tag}`;
            }

            get _pin() {
                return this.coords.pin$.misc;
            }

            expand() {
                this._pin.on(this.panelTag);
            }

            collapse() {
                this._pin.off(this.panelTag);
            }

            get isVisible() {
                return !this._pin.has(this.minimizeTag);
            }

            set isVisible(val: boolean) {
                val ? this._pin.off(this.minimizeTag) : this._pin.on(this.minimizeTag);
            }

            get isExpanded() {
                return this._pin.has(this.panelTag);
            }
        }

        return new Map<string, Panel>(this._panelInitLs.map((x) => [x, new Panel(this.coords, x)]));
    }

    private get _panelInitLs() {
        return this.coords.stage$.isPreview
            ? this._itemDetailTabLs.filter(
                  (x) => !['subsets', 'ratings', 'tags', 'comments', 'attachments'].includes(x),
              )
            : this._itemDetailTabLs.filter((x) => !['resources'].includes(x));
    }

    get hiddenPanelCount() {
        return this._panelInitLs.filter((x) => this.coords.pin$.misc!.has(`minimize-panel-${x}`))
            .length;
    }

    expandedIcon(isExpanded: boolean): string {
        return isExpanded ? 'unfold_more' : 'unfold_less';
    }

    error(action: string, message: string) {
        this.error$.next({
            message: message,
            action: action,
        });
    }

    closeDialog() {
        this.dialog$.next({
            action: 'close',
        });
    }

    closeHelpDialog() {
        this.helpDialog?.close();
    }

    moreDialog(tag: string) {
        this.dialog$.next({
            action: 'more',
            tag: tag,
        });
    }

    restartDialog() {
        this.dialog$.next({
            action: 'restart',
        });
    }

    confirm(fun: (x?: any) => void) {
        this.dialog$.next({
            action: 'confirm',
            fun: fun,
        });
    }

    icon(tag: string) {
        return this.icons.get(tag) || 'usb';
    }

    text(tag: string): string {
        if (typeof tag !== 'string') return '';
        tag = tag.trim();
        let txt = this.textMap.get(tag.toLowerCase()) || tag;

        if (txt) {
            const tUp = tag.toUpperCase();
            if (tUp == tag) {
                txt = txt.toUpperCase();
            } else if (tUp.charAt(0) == tag.charAt(0)) {
                txt = `${txt.charAt(0).toUpperCase()}${txt.slice(1)}`;
            }
            return txt;
        } else {
            return tag;
        }
    }

    get isSmallDevice() {
        return window.innerWidth < 600 * window.devicePixelRatio;
    }
}
